const config = {
    tenantId: import.meta.env.VITE_AZURE_TENANT_ID,
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    redirectUri: window.location.origin
};

// Add error handling for missing environment variables
if (!config.tenantId || !config.clientId) {
    console.error('Missing required environment variables. Please ensure VITE_AZURE_TENANT_ID and VITE_AZURE_CLIENT_ID are set.');
}

class AuthService {
    constructor() {
        this.msalConfig = {
            auth: {
                clientId: config.clientId,
                authority: `https://login.microsoftonline.com/${config.tenantId}`,
                redirectUri: config.redirectUri,
                navigateToLoginRequestUrl: false
            },
            cache: {
                cacheLocation: "localStorage",
                storeAuthStateInCookie: true
            },
            system: {
                allowNativeBroker: false,
                iframeHashTimeout: 6000,
                loggerOptions: {
                    loggerCallback: (level, message, containsPii) => {
                        if (containsPii) {
                            return;
                        }
                        switch (level) {
                            case msal.LogLevel.Error:
                                console.error(message);
                                return;
                            case msal.LogLevel.Info:
                                console.info(message);
                                return;
                            case msal.LogLevel.Verbose:
                                console.debug(message);
                                return;
                            case msal.LogLevel.Warning:
                                console.warn(message);
                                return;
                            default:
                                return;
                        }
                    }
                }
            }
        };
        this.msalInstance = null;
    }

    async initialize() {
        if (!this.msalInstance) {
            try {
                this.msalInstance = new msal.PublicClientApplication(this.msalConfig);
                await this.msalInstance.initialize();
                
                // Handle redirect promise
                const response = await this.msalInstance.handleRedirectPromise();
                if (response) {
                    // If we have a response, we're returning from a redirect
                    return response;
                }
            } catch (error) {
                console.error('MSAL initialization error:', error);
                throw error;
            }
        }
    }

    async login() {
        try {
            await this.initialize();
            
            // Check if there's already an interaction in progress
            if (this.msalInstance.getActiveAccount()) {
                return;
            }

            const loginRequest = {
                scopes: ['User.Read'],
                prompt: 'select_account'
            };
            
            await this.msalInstance.loginRedirect(loginRequest);
        } catch (error) {
            if (error.name === 'BrowserAuthError' && error.errorCode === 'interaction_in_progress') {
                // If there's already an interaction in progress, wait for it to complete
                await this.msalInstance.handleRedirectPromise();
            } else {
                console.error('Login failed:', error);
                throw error;
            }
        }
    }

    async getAccessToken() {
        try {
            await this.initialize();
            const account = this.msalInstance.getAllAccounts()[0];
            if (!account) {
                throw new Error('No active account!');
            }

            const tokenResponse = await this.msalInstance.acquireTokenSilent({
                scopes: ['User.Read'],
                account: account
            });

            return tokenResponse.accessToken;
        } catch (error) {
            if (error.name === 'InteractionRequiredAuthError') {
                await this.login();
                return null;
            }
            console.error('Error getting access token:', error);
            throw error;
        }
    }

    async logout() {
        try {
            await this.initialize();
            const logoutRequest = {
                account: this.msalInstance.getAllAccounts()[0]
            };
            await this.msalInstance.logoutRedirect(logoutRequest);
        } catch (error) {
            console.error('Logout failed:', error);
            throw error;
        }
    }

    isAuthenticated() {
        return this.msalInstance && this.msalInstance.getAllAccounts().length > 0;
    }
}

export const authService = new AuthService(); 